/** Utilidades de búsqueda insensible a acentos (misma normalización que la columna `busqueda`). */

/** Quita tildes/diacríticos y pasa a minúsculas. */
export function deaccent(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Divide una búsqueda en términos normalizados (máx. 5). Los comodines de
 * LIKE (`%`, `_`) y la barra de escape se tratan como separadores: los
 * términos van dentro de `ilike('%term%')`.
 */
export function searchTerms(q: string, max = 5): string[] {
  return deaccent(q)
    .replace(/[%_\\,()]/g, " ")
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, max);
}

/**
 * Normaliza texto para comparar en buscadores: sin tildes, minúsculas y con
 * la puntuación (apóstrofos, guiones, barras…) convertida en espacios, de
 * modo que "leganes" ≈ "Leganés" y "l hospitalet" ≈ "L'Hospitalet".
 */
export function normalizeSearch(s: string): string {
  return deaccent(s)
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/**
 * Puntuación de coincidencia de `query` en `text` (insensible a tildes).
 * Todos los términos de la búsqueda deben aparecer en el texto.
 * Devuelve -1 si no coincide; si coincide, menor = mejor:
 * 0 empieza igual, 1 alguna palabra empieza por la búsqueda, 2 contiene.
 */
export function searchScore(text: string, query: string): number {
  const q = normalizeSearch(query);
  if (!q) return 0;
  const t = normalizeSearch(text);
  const terms = q.split(" ");
  if (!terms.every((term) => t.includes(term))) return -1;
  if (t.startsWith(q)) return 0;
  if (` ${t}`.includes(` ${terms[0]}`)) return 1;
  return 2;
}

/**
 * Filtra y ordena `items` por coincidencia con `query` (ver `searchScore`),
 * conservando el orden original entre empates.
 */
export function searchRank<T>(
  items: readonly T[],
  query: string,
  text: (item: T) => string,
  limit = Infinity,
): T[] {
  return items
    .map((item, i) => ({ item, i, s: searchScore(text(item), query) }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => a.s - b.s || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.item);
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const CMDK_SCORE = [1, 0.9, 0.8];

/**
 * Filtro para cmdk (`<Command filter>`). El suyo por defecto solo pasa a
 * minúsculas ("leganes" no encontraba "Leganés") y su coincidencia difusa por
 * letras sueltas mete ruido ("cordoba" → "Cadalso de los Vidrios"). Ignora
 * los UUID que algunas listas añaden al `value` para distinguir duplicados.
 */
export function cmdkSearchFilter(value: string, search: string, keywords?: string[]): number {
  const text = [value, ...(keywords ?? [])].join(" ").replace(UUID_RE, " ");
  const s = searchScore(text, search);
  return s < 0 ? 0 : CMDK_SCORE[s];
}
